import isAfter from "date-fns/isAfter";
import * as React from "react";
import { useCallback } from "react";
import { BlockRow } from "../../../components/atoms/BlockRow";
import Loader from "../../../components/atoms/Loader";
import PrintButton from "../../../components/atoms/PrintButton";
import { AnalyticsLearningMaterialTagFilter } from "../../../components/features/AnalyticsLearningMaterialTagFilter";
import { LearningMaterialTagAnalyticsHeatMapTable } from "../../../components/features/LearningMaterialTagAnalyticsHeatMapTable";
import { Provider as ActiveHeatMapRowMapProvider } from "../../../components/features/LearningMaterialTagAnalyticsHeatMapTable/useActiveHeatMapRowMap";
import { LearningMaterialTagAnalyticsSwitcher } from "../../../components/features/LearningMaterialTagAnalyticsSwitcher";
import { StudentAnalyticsTypeTab } from "../../../components/features/StudentAnalyticsTypeTab";
import { DateRangeNavigation } from "../../../components/general/DateRangeNavigation";
import SwitchTabs from "../../../components/molecules/SwitchTabs";
import { isPushableCalendarNext } from "../../../helpers/AnalyticsHelper";
import { OutletContextProps } from "../../../helpers/RouterHelper";
import enhanceStudentsPage, {
  StudentsPageProps,
} from "../../../hocs/enhanceStudentsPage";
import { useAnalyticsFilter } from "../../../hooks/filters/useAnalyticsFilter";
import { useQueryError } from "../../../hooks/http/useQueryError";
import { useOnMainScrollAreaScroll } from "../../../hooks/useMainScrollAreaScroll";
import {
  AnalyticsTermType,
  AnalyticsType,
} from "../../../interfaces/AnalyticsTableInterface";
import DateRangeFilterInterface from "../../../interfaces/DateRangeFilterInterface";
import { OrderDirFilterType } from "../../../interfaces/FiltersInterface";
import StudentInterface from "../../../interfaces/StudentInterface";
import { defaultLearningMaterialTagFilterOptions } from "./constant";
import { LearningMaterialTagTimeAnalyticsChart } from "./LearningMaterialTagTimeAnalyticsChart";
import styles from "./StudentsLearningMaterialTagAnalyticsTimePage.scss";
import { useLearningMaterialFilterContext } from "./useLearningMaterialFilterContext";
import { useLearningMaterialTagStudyScores } from "./useLearningMaterialTagAnalyticsApi";

type UseStudentsLearningMaterialTagAnalyticsTimePageProps = {
  student: StudentInterface;
  term: AnalyticsTermType;
  startDate: Date;
  endDate: Date;
  order: string;
  orderDir: OrderDirFilterType;
  onDateFilterChange: (dateRange: DateRangeFilterInterface) => void;
};

// Main hooks
const useStudentsLearningMaterialTagAnalyticsTimePage = ({
  student,
  startDate,
  endDate,
  term,
  order,
  orderDir,
  onDateFilterChange,
}: UseStudentsLearningMaterialTagAnalyticsTimePageProps) => {
  // 毎レンダリングでtoDateすると毎回新しい日付インスタンスを作ってしまい、その度にAPIを実行してしまう。
  // それをメモ化して防ぐ。
  const dateParams = React.useMemo(
    () => ({
      startDate,
      endDate,
    }),
    [startDate, endDate],
  );

  const { items: selectedOptions, isInitialized: isDeterminedDefaultOptions } =
    useLearningMaterialFilterContext();

  const {
    hasNextPage,
    isLoading: isHeatMapLoading,
    isFetchingNextPage,
    data: heatMapData,
    fetchNextPage,
    error: heatMapError,
  } = useLearningMaterialTagStudyScores({
    startDate: dateParams.endDate, // API 側は遡って計算するため
    term,
    student,
    learningMaterialTags: selectedOptions,
    order: order ?? "",
    orderDir,
    enabledRequest: isDeterminedDefaultOptions,
  });
  useQueryError(heatMapError);

  useOnMainScrollAreaScroll(() => {
    if (hasNextPage && !isHeatMapLoading && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isHeatMapLoading, isFetchingNextPage]);

  const analyticsChartProps = React.useMemo(
    () => ({
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
      term,
      student,
      order,
      orderDir,
    }),
    [dateParams, student, term, order, orderDir],
  );

  const handleDatesChange = useCallback(
    (dates: { startDate?: Date; endDate?: Date }) => {
      if (dates.startDate && dates.endDate) {
        onDateFilterChange({
          startDate: dates.startDate,
          endDate: dates.endDate,
        });
      }
    },
    [onDateFilterChange],
  );
  const datePickerProps = React.useMemo(
    () => ({
      calendarDisabled: !term || term !== "daily",
      canPushRightButton: isPushableCalendarNext(endDate, term),
      onDatesChange: handleDatesChange,
    }),
    [endDate, term, handleDatesChange],
  );

  const heatMapLoaderProps = {
    loading:
      isHeatMapLoading || isFetchingNextPage || !isDeterminedDefaultOptions,
  };
  return {
    readyRenderContens: isDeterminedDefaultOptions,
    heatMapData,
    datePickerProps,
    heatMapLoaderProps,
    analyticsChartProps,
  };
};

type Props = StudentsPageProps &
  OutletContextProps & {
    student: StudentInterface;
    analyticsType: AnalyticsType;
  };

// Main component
export const StudentsLearningMaterialTagAnalyticsTimePage_ = ({
  student,
  analyticsType,
  setActiveMenu,
}: Props) => {
  React.useEffect(() => {
    setActiveMenu("analytics");
  }, []);

  const { analyticsFilter, changeOrder, changeDates, changeTerm } =
    useAnalyticsFilter({
      withStudentFilter: false,
    });

  const {
    analyticsChartProps,
    heatMapData,
    datePickerProps,
    heatMapLoaderProps,
    readyRenderContens,
  } = useStudentsLearningMaterialTagAnalyticsTimePage({
    student,
    startDate: analyticsFilter.startDate,
    endDate: analyticsFilter.endDate,
    term: analyticsFilter.term,
    order: analyticsFilter.order ?? "",
    orderDir: analyticsFilter.orderDir ?? "desc",
    onDateFilterChange: changeDates,
  });

  return (
    <ActiveHeatMapRowMapProvider>
      <div className={styles.TabWrapper}>
        <StudentAnalyticsTypeTab activeAnalyticsType="time" student={student} />
      </div>
      <div className={styles.TableControl}>
        <div className={styles.TableControl__LeftArea}>
          <LearningMaterialTagAnalyticsSwitcher
            student={student}
            currentAnalyticsType="time"
            value="learning_material_tag"
          />
        </div>
        <DateRangeNavigation
          startDate={analyticsFilter.startDate}
          endDate={analyticsFilter.endDate}
          term={analyticsFilter.term}
          isDayBlocked={(day: Date) => isAfter(day, new Date())}
          {...datePickerProps}
        />
        <div className={styles.TableControl__RightArea}>
          <SwitchTabs
            tabs={[
              {
                id: "daily",
                label: "日",
                active: analyticsFilter.term === "daily",
              },
              {
                id: "weekly",
                label: "週",
                active: analyticsFilter.term === "weekly",
              },
              {
                id: "monthly",
                label: "月",
                active: analyticsFilter.term === "monthly",
              },
            ]}
            onClickTab={(tab: any) => () => {
              changeTerm(tab.id);
            }}
          />
          <PrintButton />
        </div>
      </div>
      <BlockRow marginTop="2.0rem">
        <AnalyticsLearningMaterialTagFilter
          context={useLearningMaterialFilterContext()}
          section={student.section}
          defaultLearningMaterialTagFilterOptions={
            defaultLearningMaterialTagFilterOptions
          }
          isInitializeByQueryParams={true}
          isUseLocalStorage={false}
        />
      </BlockRow>
      {readyRenderContens && (
        <div className={styles.MainContentsWrapper}>
          <LearningMaterialTagTimeAnalyticsChart {...analyticsChartProps} />
          <div className={styles.TableWrapper}>
            <LearningMaterialTagAnalyticsHeatMapTable
              learningMaterialTagAnalyticsHeatMap={heatMapData}
              order={analyticsFilter.order}
              orderDir={analyticsFilter.orderDir}
              onOrderChange={(order, orderDir) =>
                changeOrder(order ?? null, orderDir ?? "desc")
              }
              analyticsType={analyticsType}
              student={student}
            />
            <Loader {...heatMapLoaderProps} />
          </div>
        </div>
      )}
    </ActiveHeatMapRowMapProvider>
  );
};

export const StudentsLearningMaterialTagAnalyticsTimePage = enhanceStudentsPage(
  StudentsLearningMaterialTagAnalyticsTimePage_,
  {
    title: "アナリティクス",
  },
);
